/**
 * usePriceSegmentsRef — Référentiel price_segments (6 canonical rows).
 *
 * GET /api/v1/referentials/price-segments
 *
 * Distinct du hook `usePriceSegments` (KPI B.8 — Distribution Price Segments,
 * endpoint /market/price-segments). Ici on consomme le référentiel pour
 * alimenter le sélecteur "Segment prix" de la ScopeBar (MX RM Flow).
 *
 * Shape backend (snake_case) :
 *   { id, code, label, min_usd, max_usd, order_index, category_id, category }
 * On expose en camelCase + on filtre les champs utiles UI.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

export interface PriceSegmentRef {
  id:     string;
  code:   string;
  label:  string;
  minUsd: number | null;
  maxUsd: number | null;
}

interface PriceSegmentApiRow {
  id:           string;
  code:         string;
  label:        string;
  min_usd:      string | null;
  max_usd:      string | null;
  order_index?: number;
  category_id?: string;
  category?:    { code: string; name: string };
}

/** QueryKey canonique — exporté pour réutilisation par useScopeBarPrefetch. */
export const PRICE_SEGMENTS_REF_KEY = ['referentials', 'price-segments'] as const;

/** QueryFn canonique — exporté pour garantir un shape de cache identique au hook. */
export async function fetchPriceSegmentsRef(): Promise<PriceSegmentRef[]> {
  const res = await apiClient.get<PriceSegmentApiRow[]>('/referentials/price-segments');
  if (!Array.isArray(res.data)) return [];
  return res.data.map((r) => ({
    id:     r.id,
    code:   r.code,
    label:  r.label,
    minUsd: r.min_usd != null ? Number(r.min_usd) : null,
    maxUsd: r.max_usd != null ? Number(r.max_usd) : null,
  }));
}

export function usePriceSegmentsRef() {
  return useQuery<PriceSegmentRef[]>({
    queryKey: PRICE_SEGMENTS_REF_KEY,
    queryFn: fetchPriceSegmentsRef,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}
